/**
 * Intent Processor for Scenario Builder
 *
 * Converts high-level intent objects (from LLM) into low-level scenario patches.
 * Implementation based on flow-designer architecture patterns.
 *
 * Key Patterns from flow-designer:
 * - Bidirectional linking via nextNodes/prevNodes arrays
 * - Hierarchical branchId structure (1, 1-1, 1-2, 1-2-1, etc.)
 * - Factory pattern for node creation
 * - Safety checks before deletion
 * - Automatic edge reconnection
 */

import { v4 as uuidv4 } from 'uuid';

/**
 * Processes high-level intent objects into scenario patches
 */
export class ScenarioIntentProcessor {
  /**
   * @param {Object} scenario - Current scenario state (hash table of nodes)
   * @param {Object} nodeDefinitions - Node type definitions
   * @param {Array} tasks - Available tasks
   */
  constructor(scenario, nodeDefinitions, tasks) {
    // Create a working copy of the scenario to track changes during processing
    // This ensures subsequent operations see the updated state
    this.scenario = JSON.parse(JSON.stringify(scenario));
    this.nodeDefinitions = nodeDefinitions;
    this.tasks = tasks;

    // Track node IDs created during processing (for sequential operations)
    this.createdNodes = new Map(); // key: <<PREVIOUS_NODE_ID>> → actual UUID
    this.lastCreatedNodeId = null;
  }

  /**
   * Apply patches to internal scenario state
   * This ensures subsequent operations see the updated state
   *
   * @param {Object} patches - Patches to apply { add, update, remove }
   */
  applyPatchesToInternalState(patches) {
    // Apply additions
    if (patches.add) {
      for (const [nodeId, nodeData] of Object.entries(patches.add)) {
        this.scenario[nodeId] = nodeData;
      }
    }

    // Apply updates
    if (patches.update) {
      for (const [nodeId, updates] of Object.entries(patches.update)) {
        if (!this.scenario[nodeId]) {
          console.warn(`[ScenarioIntentProcessor] Cannot update non-existent node: ${nodeId}`);
          continue;
        }

        // Update data fields
        if (updates.data) {
          this.scenario[nodeId].data = {
            ...this.scenario[nodeId].data,
            ...updates.data
          };
        }

        // Update links
        if (updates.links) {
          if (updates.links.nextNodes) {
            this.scenario[nodeId].nextNodes = updates.links.nextNodes;
          }
          if (updates.links.prevNodes) {
            this.scenario[nodeId].prevNodes = updates.links.prevNodes;
          }
        }
      }
    }

    // Apply removals
    if (patches.remove && Array.isArray(patches.remove)) {
      for (const nodeId of patches.remove) {
        delete this.scenario[nodeId];
      }
    }
  }

  /**
   * Main entry point: convert intent to patches
   *
   * @param {Object} intent - Intent object from LLM
   * @param {Array} intent.operations - Array of operations to perform
   * @returns {Object} - { patches, errors }
   */
  processIntent(intent) {
    console.log('[ScenarioIntentProcessor] Processing intent:', intent);

    const patches = {
      add: {},
      update: {},
      remove: []
    };
    const errors = [];

    // Validate intent structure
    if (!intent || typeof intent !== 'object') {
      errors.push('Invalid intent: not an object');
      return { patches, errors };
    }

    if (!Array.isArray(intent.operations)) {
      errors.push('Invalid intent: operations must be an array');
      return { patches, errors };
    }

    if (intent.operations.length === 0) {
      errors.push('Invalid intent: operations array is empty');
      return { patches, errors };
    }

    // Process each operation sequentially, updating internal state after each
    // This is CRITICAL for batch operations like "remove last 5 nodes"
    for (let i = 0; i < intent.operations.length; i++) {
      const operation = intent.operations[i];

      // Track patches for this specific operation
      const operationPatches = {
        add: {},
        update: {},
        remove: []
      };

      try {
        switch (operation.type) {
          case 'ADD_NODE':
            this.handleAddNode(operation, operationPatches);
            break;
          case 'UPDATE_NODE':
            this.handleUpdateNode(operation, operationPatches);
            break;
          case 'REMOVE_NODE':
            this.handleRemoveNode(operation, operationPatches);
            break;
          case 'ADD_BRANCH':
            this.handleAddBranch(operation, operationPatches);
            break;
          case 'REMOVE_BRANCH':
            this.handleRemoveBranch(operation, operationPatches);
            break;
          default:
            errors.push(`Unknown operation type: ${operation.type}`);
            continue;
        }

        // CRITICAL: Apply patches to internal state so next operation sees updated scenario
        this.applyPatchesToInternalState(operationPatches);

        // Merge operation patches into final patches
        Object.assign(patches.add, operationPatches.add);
        Object.assign(patches.update, operationPatches.update);
        patches.remove.push(...operationPatches.remove);

      } catch (err) {
        errors.push(`Error processing ${operation.type}: ${err.message}`);
        console.error(`[ScenarioIntentProcessor] Error:`, err);
      }
    }

    // Log errors if any
    if (errors.length > 0) {
      console.error('[ScenarioIntentProcessor] Errors:', errors);
    }

    return { patches, errors };
  }

  /**
   * Handle ADD_NODE operation
   *
   * @param {Object} operation - ADD_NODE operation
   * @param {Object} patches - Patches object to populate
   */
  handleAddNode(operation, patches) {
    console.log('[ScenarioIntentProcessor] handleAddNode:', operation);

    // 0. Validate that conditional nodes are not created via ADD_NODE
    if (operation.nodeType && operation.nodeType.startsWith('if:')) {
      throw new Error(`Cannot create conditional node "${operation.nodeType}" using ADD_NODE. Use ADD_BRANCH operation instead.`);
    }

    // 1. Generate UUID for new node
    const newNodeId = this.generateUUID();

    // 2. Resolve position to actual node IDs
    const position = this.resolvePosition(operation.position, patches);

    // 3. Get default data for node type
    const defaultData = this.getDefaultDataForNodeType(operation.nodeType);

    // 4. Process special node types that have complex data structures
    let processedData = { ...operation.data };

    // Handle message:suggestions nodes
    if (operation.nodeType === 'message:suggestions') {
      processedData.articles = this.createArticlesStructure(operation.data);
    }

    // Handle fit:facts nodes
    if (operation.nodeType === 'fit:facts') {
      processedData.facts = this.createFactsStructure(operation.data);
    }

    // 5. Merge operation data with defaults
    const nodeData = {
      ...defaultData,
      ...processedData,
      nodeType: operation.nodeType,
      deserialized: true
    };

    // 6. Create node with proper structure (based on flow-designer pattern)
    const newNode = {
      id: newNodeId,
      type: this.getNodeTypeClass(operation.nodeType),
      branchId: position.branchId,
      parentBranchId: position.parentBranchId || '',
      parentNodeId: position.prevNodeId,
      data: nodeData,
      nextNodes: [position.nextNodeId],
      prevNodes: [position.prevNodeId]
    };

    // 7. Add to patches
    patches.add[newNodeId] = newNode;

    // 8. Update bidirectional links (flow-designer pattern)
    this.updateBidirectionalLinks(
      patches,
      position.prevNodeId,
      position.nextNodeId,
      newNodeId
    );

    // 9. Track for sequential operations
    this.lastCreatedNodeId = newNodeId;

    console.log(`[ScenarioIntentProcessor] Created node: ${newNodeId}`);
  }

  /**
   * Handle UPDATE_NODE operation
   *
   * @param {Object} operation - UPDATE_NODE operation
   * @param {Object} patches - Patches object to populate
   */
  handleUpdateNode(operation, patches) {
    console.log('[ScenarioIntentProcessor] handleUpdateNode:', operation);

    const nodeId = operation.nodeId;

    // Validate node exists
    if (!this.scenario[nodeId]) {
      throw new Error(`Node ${nodeId} not found in scenario`);
    }

    const node = this.scenario[nodeId];
    const nodeType = node.data?.nodeType;

    // Initialize update entry if needed
    if (!patches.update[nodeId]) {
      patches.update[nodeId] = { set: {} };
    }

    // Check if trying to directly update articles/facts structure (not allowed - must use simplified fields)
    if ((nodeType === 'message:suggestions' && operation.updates['data.articles']) ||
        (nodeType === 'fit:facts' && operation.updates['data.facts'])) {
      throw new Error(
        `Cannot directly update ${nodeType === 'message:suggestions' ? 'data.articles' : 'data.facts'} field. ` +
        `Use simplified fields instead: ` +
        `For dynamic selection, use "data.categoryIds" and/or "data.tagIds". ` +
        `For individual selection, use "data.${nodeType === 'message:suggestions' ? 'articleIds' : 'factIds'}".`
      );
    }

    // Check if this is a suggestions or facts node update with simplified fields
    const hasSimplifiedFields = operation.updates && (
      operation.updates['data.articleIds'] ||
      operation.updates['data.categoryIds'] ||
      operation.updates['data.tagIds'] ||
      operation.updates['data.factIds'] ||
      operation.updates['data.maxNumber']
    );

    // If updating a suggestions/facts node with simplified fields, rebuild the structure
    if (hasSimplifiedFields && (nodeType === 'message:suggestions' || nodeType === 'fit:facts')) {
      // Extract all data updates
      const dataUpdates = {};
      Object.entries(operation.updates).forEach(([path, value]) => {
        if (path.startsWith('data.')) {
          const fieldName = path.replace('data.', '');
          dataUpdates[fieldName] = value;
        }
      });

      // Get current node data but EXCLUDE the old articles/facts object AND old simplified fields
      // We want to rebuild it from scratch based on the NEW simplified fields
      const currentDataWithoutComplex = { ...node.data };
      if (nodeType === 'message:suggestions') {
        delete currentDataWithoutComplex.articles;
        // Also remove old simplified fields so they don't conflict with new ones
        delete currentDataWithoutComplex.articleIds;
        delete currentDataWithoutComplex.categoryIds;
        delete currentDataWithoutComplex.tagIds;
        delete currentDataWithoutComplex.maxNumber;
      } else if (nodeType === 'fit:facts') {
        delete currentDataWithoutComplex.facts;
        // Also remove old simplified fields
        delete currentDataWithoutComplex.factIds;
        delete currentDataWithoutComplex.categoryIds;
        delete currentDataWithoutComplex.tagIds;
      }

      // Merge with updates - now only the NEW simplified fields will be present
      const currentData = { ...currentDataWithoutComplex, ...dataUpdates };

      // Rebuild the articles/facts structure from simplified fields
      if (nodeType === 'message:suggestions') {
        const articlesStructure = this.createArticlesStructure(currentData);
        console.log('[ScenarioIntentProcessor] Rebuilt articles structure:', JSON.stringify(articlesStructure, null, 2));
        console.log('[ScenarioIntentProcessor] Current data used:', JSON.stringify(currentData, null, 2));
        patches.update[nodeId].set['data.articles'] = articlesStructure;

        // Remove the simplified fields from the update (they were just for input)
        // Keep other fields like title, message, etc.
        Object.entries(operation.updates).forEach(([path, value]) => {
          if (!path.startsWith('data.articleIds') &&
              !path.startsWith('data.categoryIds') &&
              !path.startsWith('data.tagIds') &&
              !path.startsWith('data.maxNumber')) {
            patches.update[nodeId].set[path] = value;
          }
        });
      } else if (nodeType === 'fit:facts') {
        const factsStructure = this.createFactsStructure(currentData);
        patches.update[nodeId].set['data.facts'] = factsStructure;

        // Remove the simplified fields from the update
        Object.entries(operation.updates).forEach(([path, value]) => {
          if (!path.startsWith('data.factIds') &&
              !path.startsWith('data.categoryIds') &&
              !path.startsWith('data.tagIds')) {
            patches.update[nodeId].set[path] = value;
          }
        });
      }
    } else {
      // Standard update: Apply all updates using dot-path notation
      Object.entries(operation.updates).forEach(([path, value]) => {
        patches.update[nodeId].set[path] = value;
      });
    }

    console.log(`[ScenarioIntentProcessor] Updated node: ${nodeId}`);
  }

  /**
   * Handle REMOVE_NODE operation
   *
   * @param {Object} operation - REMOVE_NODE operation
   * @param {Object} patches - Patches object to populate
   */
  handleRemoveNode(operation, patches) {
    console.log('[ScenarioIntentProcessor] handleRemoveNode:', operation);

    const nodeId = operation.nodeId;
    const node = this.scenario[nodeId];

    // Validate node exists
    if (!node) {
      throw new Error(`Node ${nodeId} not found in scenario`);
    }

    // Safety check: Silently skip removing StartNode or FinishNode
    // This allows batch operations like "remove last 5 nodes" to work even if one is StartNode/FinishNode
    if (node.type === 'StartNode' || node.type === 'FinishNode') {
      console.warn(`[ScenarioIntentProcessor] Skipping removal of ${node.type} (ID: ${nodeId}) - these nodes are required and cannot be deleted.`);
      return; // Skip this operation, continue with others
    }

    // Add to remove list
    patches.remove.push(nodeId);

    // Reconnect adjacent nodes (flow-designer pattern: incomers → outgoers)
    const prevNodeId = node.prevNodes?.[0];
    const nextNodeId = node.nextNodes?.[0];

    if (prevNodeId && nextNodeId) {
      // Update previous node's nextNodes
      if (!patches.update[prevNodeId]) {
        patches.update[prevNodeId] = { links: {} };
      }
      patches.update[prevNodeId].links.nextNodes = [nextNodeId];

      // Update next node's prevNodes
      if (!patches.update[nextNodeId]) {
        patches.update[nextNodeId] = { links: {} };
      }
      patches.update[nextNodeId].links.prevNodes = [prevNodeId];
    }

    console.log(`[ScenarioIntentProcessor] Removed node: ${nodeId}`);
  }

  /**
   * Handle ADD_BRANCH operation
   *
   * Creates IfConditionNode + IfBranchNodes + IfCloseNode
   *
   * @param {Object} operation - ADD_BRANCH operation
   * @param {Object} patches - Patches object to populate
   */
  handleAddBranch(operation, patches) {
    console.log('[ScenarioIntentProcessor] handleAddBranch:', operation);

    // Resolve <<PREVIOUS_NODE_ID>> in conditionSource if present
    let conditionSource = operation.conditionSource;
    if (conditionSource === '<<PREVIOUS_NODE_ID>>') {
      if (!this.lastCreatedNodeId) {
        throw new Error('Cannot use <<PREVIOUS_NODE_ID>> for conditionSource - no node was previously created');
      }
      conditionSource = this.lastCreatedNodeId;
      console.log('[ScenarioIntentProcessor] Resolved <<PREVIOUS_NODE_ID>> in conditionSource to:', conditionSource);
    }

    // Resolve position
    const position = operation.after
      ? this.resolvePosition({ after: operation.after }, patches)
      : this.resolvePosition({ start: true }, patches);

    // Validate conditionSource exists (check both scenario and newly created patches)
    let condSourceNode = this.scenario[conditionSource];
    if (!condSourceNode && patches.add && patches.add[conditionSource]) {
      condSourceNode = patches.add[conditionSource];
    }
    if (!condSourceNode) {
      throw new Error(`Condition source node ${conditionSource} not found`);
    }

    // Check if conditionSource is an existing if statement
    // If so, delegate to specialized handler
    if (condSourceNode.type === 'IfConditionBehaviorNode' || condSourceNode.type === 'IfConditionNode') {
      console.log('[ScenarioIntentProcessor] Detected ADD_BRANCH on existing if statement, delegating to specialized handler');
      this.handleAddBranchToExistingIf(operation, patches);
      return;
    }

    // Validate conditionSource has response options (ONLY choice-based questions supported for NEW if statements)
    if (!condSourceNode.data?.response || !Array.isArray(condSourceNode.data.response)) {
      const nodeType = condSourceNode.data?.nodeType || condSourceNode.nodeType || condSourceNode.type || 'unknown';
      const nodeLabel = condSourceNode.data?.label || condSourceNode.data?.title || condSourceNode.data?.message || 'unlabeled';

      throw new Error(
        `Cannot create branch: Node "${nodeLabel}" (${conditionSource}) is type "${nodeType}" which does not support branching. ` +
        `Only choice questions (message:single or message:multi) with response options can be used to create NEW if statements. ` +
        `Input fields (message:input, message:date, message:picker, message:slider) are NOT supported. ` +
        `Check the "branchableNodes" array in the snapshot to see which nodes support branching.`
      );
    }

    // Generate IDs
    const condNodeId = this.generateUUID();
    const branchIds = operation.branches.map(() => this.generateUUID());
    const closeNodeId = this.generateUUID();

    // Build condStatements from branches
    const condStatements = operation.branches.map((branch) => {
      if (branch.condition.else) {
        return {
          op: 'else',
          value: null
        };
      }

      // LF360-1838: `contains` is accepted as a synonym for `equals`.
      //
      // When the condition source is a multi-select, "was this option picked"
      // is a membership test, and that is the word the model reaches for. The
      // grammar only had `equals`, so any questionnaire branching on a
      // multi-select failed - either the semantic validator burned all three
      // attempts, or it let the intent through and this line threw during
      // apply, which is what the participant saw as an error.
      //
      // They compile to the same thing on purpose. `{op: 'or', value: [{op:
      // 'eq', ...}]}` is flattened by the mobile engine to a list of values and
      // matched with `answers.some(a => list.includes(a.value))` for a
      // multi-select answer, so `equals` was ALREADY a membership test there.
      // Nothing about the stored scenario or the runtime changes here - only
      // which word the author-facing grammar will accept.
      const matchValue = branch.condition.equals ?? branch.condition.contains;

      if (matchValue !== undefined) {
        return {
          op: 'or',
          value: [{ op: 'eq', value: matchValue }]
        };
      }

      throw new Error('Invalid branch condition: must have "equals", "contains" or "else" field');
    });

    // 1. Create IfConditionNode
    const ifConditionNode = {
      id: condNodeId,
      type: 'IfConditionNode',
      branchId: position.branchId,
      parentBranchId: position.parentBranchId || '',
      parentNodeId: position.prevNodeId,
      data: {
        label: 'If Condition',
        nodeType: 'if:condition',
        message: '',
        condStatements: condStatements,
        condSourceId: conditionSource, // Use resolved conditionSource
        deserialized: true
      },
      nextNodes: branchIds,
      prevNodes: [position.prevNodeId]
    };

    patches.add[condNodeId] = ifConditionNode;

    // 2. Create IfBranchNodes and their content
    const branchEndNodeIds = []; // Track end of each branch for IfCloseNode

    operation.branches.forEach((branch, index) => {
      const branchNodeId = branchIds[index];
      const branchNumber = index + 1;
      const subBranchId = `${position.branchId}-${branchNumber}`;

      // Create IfBranchNode
      const ifBranchNode = {
        id: branchNodeId,
        type: 'IfBranchNode',
        branchId: subBranchId,
        parentBranchId: position.branchId,
        parentNodeId: condNodeId,
        data: {
          label: `If Condition-${branchNumber}`,
          nodeType: 'if:branch',
          message: `Branch ${branchNumber}`,
          dependsOn: condNodeId,
          deserialized: true
        },
        nextNodes: [], // Will be set after processing branch content
        prevNodes: [condNodeId]
      };

      patches.add[branchNodeId] = ifBranchNode;

      // Process nodes within this branch
      let currentPrevId = branchNodeId;

      if (branch.nodes && branch.nodes.length > 0) {
        branch.nodes.forEach((nodeOp) => {
          if (nodeOp.type === 'ADD_NODE') {
            // Create node in this branch
            const branchContentNodeId = this.generateUUID();
            const defaultData = this.getDefaultDataForNodeType(nodeOp.nodeType);

            // Process special node types that have complex data structures
            let processedData = { ...nodeOp.data };

            // Handle message:suggestions nodes
            if (nodeOp.nodeType === 'message:suggestions') {
              processedData.articles = this.createArticlesStructure(nodeOp.data);
            }

            // Handle fit:facts nodes
            if (nodeOp.nodeType === 'fit:facts') {
              processedData.facts = this.createFactsStructure(nodeOp.data);
            }

            const branchContentNode = {
              id: branchContentNodeId,
              type: this.getNodeTypeClass(nodeOp.nodeType),
              branchId: subBranchId,
              parentBranchId: position.branchId,
              parentNodeId: currentPrevId,
              data: {
                ...defaultData,
                ...processedData,
                nodeType: nodeOp.nodeType,
                deserialized: true
              },
              nextNodes: [], // Will be set to next node or IfCloseNode
              prevNodes: [currentPrevId]
            };

            patches.add[branchContentNodeId] = branchContentNode;

            // Update previous node's nextNodes
            if (patches.add[currentPrevId]) {
              patches.add[currentPrevId].nextNodes = [branchContentNodeId];
            }

            currentPrevId = branchContentNodeId;
          }
        });
      }

      // Track the end of this branch
      branchEndNodeIds.push(currentPrevId);

      // Set the last node in branch to point to IfCloseNode (will be set below)
      if (patches.add[currentPrevId]) {
        patches.add[currentPrevId].nextNodes = [closeNodeId];
      }
    });

    // 3. Create IfCloseNode
    const ifCloseNode = {
      id: closeNodeId,
      type: 'IfCloseNode',
      branchId: position.branchId,
      parentBranchId: position.parentBranchId || '',
      parentNodeId: branchEndNodeIds[0], // First branch end node
      data: {
        label: 'If Condition-close',
        nodeType: 'if:condition-close',
        message: 'If condition close',
        dependsOn: condNodeId,
        deserialized: true
      },
      nextNodes: [position.nextNodeId],
      prevNodes: branchEndNodeIds // All branch end nodes converge here
    };

    patches.add[closeNodeId] = ifCloseNode;

    // 4. Update bidirectional links for the surrounding nodes
    if (!patches.update[position.prevNodeId]) {
      patches.update[position.prevNodeId] = { links: {} };
    }
    patches.update[position.prevNodeId].links.nextNodes = [condNodeId];

    if (!patches.update[position.nextNodeId]) {
      patches.update[position.nextNodeId] = { links: {} };
    }
    patches.update[position.nextNodeId].links.prevNodes = [closeNodeId];

    console.log(`[ScenarioIntentProcessor] Created branching: ${condNodeId} with ${branchIds.length} branches`);
  }

  /**
   * Add a new branch to an existing if statement (IfConditionNode or IfConditionBehaviorNode)
   * This handles adding branches to already-existing if statements.
   *
   * @param {Object} operation - ADD_BRANCH operation
   * @param {Object} patches - Patches object to populate
   */
  handleAddBranchToExistingIf(operation, patches) {
    console.log('[ScenarioIntentProcessor] handleAddBranchToExistingIf:', operation);

    const ifConditionNodeId = operation.conditionSource;
    const ifConditionNode = this.scenario[ifConditionNodeId];

    if (!ifConditionNode) {
      throw new Error(`If condition node ${ifConditionNodeId} not found`);
    }

    // Validate it's actually an if condition node
    if (ifConditionNode.type !== 'IfConditionNode' && ifConditionNode.type !== 'IfConditionBehaviorNode') {
      throw new Error(`Node ${ifConditionNodeId} is not an if condition node (type: ${ifConditionNode.type})`);
    }

    // Find the IfCloseNode
    const ifCloseNode = this.findIfCloseNode(ifConditionNodeId);
    if (!ifCloseNode) {
      throw new Error(`Could not find IfCloseNode for condition node ${ifConditionNodeId}`);
    }

    // Get the first branch from the operation
    if (!operation.branches || operation.branches.length === 0) {
      throw new Error('ADD_BRANCH operation must have at least one branch');
    }

    const newBranchData = operation.branches[0]; // For now, support adding one branch at a time

    // Generate new branch ID
    const newBranchId = this.generateUUID();

    // Determine branchId for the new branch
    // Get existing branch IDs from current nextNodes
    const existingBranches = ifConditionNode.nextNodes || [];
    const parentBranchId = ifConditionNode.branchId || '';
    const branchIndex = existingBranches.length + 1;
    const newBranchIdValue = parentBranchId ? `${parentBranchId}-${branchIndex}` : `${branchIndex}`;

    // Create the new IfBranchNode
    const branchLabel = ifConditionNode.data?.title || ifConditionNode.data?.label || 'Branch';
    const branchNode = {
      id: newBranchId,
      type: 'IfBranchNode',
      branchId: newBranchIdValue,
      data: {
        label: `${branchLabel}-${branchIndex}`,
        message: `Branch ${branchIndex}`,
        nodeType: 'if:branch',
        dependsOn: ifConditionNodeId,
        deserialized: true,
      },
      nextNodes: [ifCloseNode.id],
      prevNodes: [ifConditionNodeId],
      parentNodeId: ifConditionNodeId,
      parentBranchId: parentBranchId,
    };

    patches.add[newBranchId] = branchNode;

    // Add content nodes inside the branch if specified
    if (newBranchData.nodes && newBranchData.nodes.length > 0) {
      let previousNodeId = newBranchId;

      for (const nodeOp of newBranchData.nodes) {
        const contentNodeId = this.generateUUID();
        const defaultData = this.getDefaultDataForNodeType(nodeOp.nodeType);

        // Process special node types that have complex data structures
        let processedData = { ...(nodeOp.data || {}) };

        // Handle message:suggestions nodes
        if (nodeOp.nodeType === 'message:suggestions') {
          processedData.articles = this.createArticlesStructure(nodeOp.data || {});
        }

        // Handle fit:facts nodes
        if (nodeOp.nodeType === 'fit:facts') {
          processedData.facts = this.createFactsStructure(nodeOp.data || {});
        }

        const contentNode = {
          id: contentNodeId,
          type: 'GenericNode',
          branchId: newBranchIdValue,
          data: {
            ...defaultData,
            ...processedData,
            nodeType: nodeOp.nodeType,
            deserialized: true,
          },
          nextNodes: previousNodeId === newBranchId ? [ifCloseNode.id] : [],
          prevNodes: [previousNodeId],
          parentNodeId: previousNodeId,
        };

        patches.add[contentNodeId] = contentNode;

        // Update previous node to point to this one
        if (previousNodeId === newBranchId) {
          // Update branch node to point to first content node instead of close node
          patches.add[newBranchId].nextNodes = [contentNodeId];
        } else {
          // Update previous content node to point to this one
          patches.add[previousNodeId].nextNodes = [contentNodeId];
        }

        // Last content node points to close node
        if (newBranchData.nodes.indexOf(nodeOp) === newBranchData.nodes.length - 1) {
          contentNode.nextNodes = [ifCloseNode.id];
        }

        previousNodeId = contentNodeId;
      }
    }

    // Update the if condition node's nextNodes to include the new branch
    const updatedNextNodes = [...(ifConditionNode.nextNodes || []), newBranchId];

    if (!patches.update[ifConditionNodeId]) {
      patches.update[ifConditionNodeId] = { set: {}, links: {} };
    }
    patches.update[ifConditionNodeId].links.nextNodes = updatedNextNodes;

    // For IfConditionBehaviorNode, add a new condition
    if (ifConditionNode.type === 'IfConditionBehaviorNode') {
      // Get existing conditions
      const existingConditions = ifConditionNode.data?.conditions || [];

      // Create new condition based on the branch operation
      let newCondition;

      if (newBranchData.condition?.else) {
        // Else branch
        newCondition = { type: 'else' };
      } else if (newBranchData.condition?.rules) {
        // Custom rules (if user provides them)
        newCondition = { rules: newBranchData.condition.rules };
      } else {
        // Default: create a simple condition (user will need to configure it in UI)
        newCondition = {
          rules: {
            criteria: [],
            operator: 'AND',
          },
        };
      }

      // Add new condition before any existing "else" condition
      const elseIndex = existingConditions.findIndex(c => c.type === 'else');
      let updatedConditions;

      if (elseIndex !== -1) {
        // Insert before else
        updatedConditions = [
          ...existingConditions.slice(0, elseIndex),
          newCondition,
          ...existingConditions.slice(elseIndex),
        ];
      } else {
        // Append to end
        updatedConditions = [...existingConditions, newCondition];
      }

      // Update conditions in patches
      if (!patches.update[ifConditionNodeId].set) {
        patches.update[ifConditionNodeId].set = {};
      }
      patches.update[ifConditionNodeId].set['data.conditions'] = updatedConditions;
    }

    // Update IfCloseNode's prevNodes to include the new branch
    const updatedClosePrevNodes = [...(ifCloseNode.prevNodes || []), newBranchId];

    if (!patches.update[ifCloseNode.id]) {
      patches.update[ifCloseNode.id] = { set: {}, links: {} };
    }
    patches.update[ifCloseNode.id].links.prevNodes = updatedClosePrevNodes;

    console.log(`[ScenarioIntentProcessor] Added new branch ${newBranchId} to existing if statement ${ifConditionNodeId}`);
  }

  /**
   * Handle REMOVE_BRANCH operation
   *
   * Removes an entire if statement (condition + branches + close).
   * Adapted from flow-designer's deleteStatementChildren logic.
   *
   * @param {Object} operation - REMOVE_BRANCH operation
   * @param {Object} patches - Patches object to populate
   */
  handleRemoveBranch(operation, patches) {
    console.log('[ScenarioIntentProcessor] handleRemoveBranch:', operation);

    const ifConditionNodeId = operation.nodeId;
    const ifConditionNode = this.scenario[ifConditionNodeId];

    // Validate node exists
    if (!ifConditionNode) {
      throw new Error(`Node ${ifConditionNodeId} not found in scenario`);
    }

    // Validate it's an IfConditionNode or IfConditionBehaviorNode
    if (ifConditionNode.type !== 'IfConditionNode' && ifConditionNode.type !== 'IfConditionBehaviorNode') {
      throw new Error(`Node ${ifConditionNodeId} is not a conditional node (type: ${ifConditionNode.type}). REMOVE_BRANCH only works with IfConditionNode or IfConditionBehaviorNode.`);
    }

    // Find the IfCloseNode (similar to getNearestNodeByType in flow-designer)
    const ifCloseNode = this.findIfCloseNode(ifConditionNodeId);
    if (!ifCloseNode) {
      throw new Error(`Could not find IfCloseNode for condition node ${ifConditionNodeId}`);
    }

    // Collect all nodes between IfConditionNode and IfCloseNode (similar to getNodesBetween)
    const nodesInsideIfStatement = this.getNodesBetween(ifConditionNodeId, ifCloseNode.id);

    console.log(`[ScenarioIntentProcessor] Found ${nodesInsideIfStatement.length} nodes in if statement`);
    console.log(`[ScenarioIntentProcessor] Nodes to remove:`, [ifConditionNodeId, ...nodesInsideIfStatement, ifCloseNode.id]);

    // Helper function to check if a node can be safely removed
    const canRemove = (nodeId) => {
      const node = this.scenario[nodeId];
      if (!node) return false;
      if (node.type === 'StartNode' || node.type === 'FinishNode') {
        console.warn(`[ScenarioIntentProcessor] Skipping removal of ${node.type} (ID: ${nodeId}) in REMOVE_BRANCH - these nodes are required.`);
        return false;
      }
      return true;
    };

    // Add all nodes to remove list (skip StartNode/FinishNode)
    if (canRemove(ifConditionNodeId)) {
      patches.remove.push(ifConditionNodeId);
    }
    nodesInsideIfStatement.forEach(nodeId => {
      if (canRemove(nodeId)) {
        patches.remove.push(nodeId);
      }
    });
    if (canRemove(ifCloseNode.id)) {
      patches.remove.push(ifCloseNode.id);
    }

    // Reconnect: node before IfConditionNode -> node after IfCloseNode
    const prevNodeId = ifConditionNode.prevNodes?.[0];
    const nextNodeId = ifCloseNode.nextNodes?.[0];

    if (prevNodeId && nextNodeId) {
      // Update previous node's nextNodes
      if (!patches.update[prevNodeId]) {
        patches.update[prevNodeId] = { links: {} };
      }
      patches.update[prevNodeId].links.nextNodes = [nextNodeId];

      // Update next node's prevNodes
      if (!patches.update[nextNodeId]) {
        patches.update[nextNodeId] = { links: {} };
      }
      patches.update[nextNodeId].links.prevNodes = [prevNodeId];
    }

    console.log(`[ScenarioIntentProcessor] Removed if statement: ${ifConditionNodeId} (${nodesInsideIfStatement.length + 2} nodes total)`);
  }

  /**
   * Find the IfCloseNode associated with an IfConditionNode
   * Adapted from flow-designer's getNearestNodeByType
   *
   * @param {string} ifConditionNodeId - IfConditionNode ID
   * @returns {Object|null} - IfCloseNode or null if not found
   */
  findIfCloseNode(ifConditionNodeId) {
    // Look for IfCloseNode with dependsOn matching this condition node
    for (const node of Object.values(this.scenario)) {
      if (node.type === 'IfCloseNode' && node.data?.dependsOn === ifConditionNodeId) {
        return node;
      }
    }

    return null;
  }

  /**
   * Get all nodes between start and end (inclusive of branch content, exclusive of start/end)
   * Adapted from flow-designer's getNodesBetween
   *
   * @param {string} startNodeId - Start node ID (IfConditionNode)
   * @param {string} endNodeId - End node ID (IfCloseNode)
   * @returns {Array} - Array of node IDs between start and end
   */
  getNodesBetween(startNodeId, endNodeId) {
    const result = [];
    const visited = new Set();

    const traverse = (nodeId) => {
      // Stop if we've reached the end node
      if (nodeId === endNodeId) {
        return;
      }

      // Stop if we've already visited this node
      if (visited.has(nodeId)) {
        return;
      }

      visited.add(nodeId);
      const node = this.scenario[nodeId];

      if (!node) {
        return;
      }

      // Add to result if it's not the start node
      if (nodeId !== startNodeId) {
        result.push(nodeId);
      }

      // Recursively traverse nextNodes
      if (node.nextNodes && Array.isArray(node.nextNodes)) {
        for (const nextNodeId of node.nextNodes) {
          traverse(nextNodeId);
        }
      }
    };

    // Start traversal from the IfConditionNode
    const startNode = this.scenario[startNodeId];
    if (startNode && startNode.nextNodes) {
      for (const nextNodeId of startNode.nextNodes) {
        traverse(nextNodeId);
      }
    }

    return result;
  }

  /**
   * Resolve position hint to actual node IDs
   *
   * @param {Object} position - Position hint (after/before/start/end)
   * @param {Object} patches - Patches object (to check newly created nodes)
   * @returns {Object} - { prevNodeId, nextNodeId, branchId, parentBranchId }
   */
  resolvePosition(position, patches) {
    console.log('[ScenarioIntentProcessor] resolvePosition:', position);

    // Handle <<PREVIOUS_NODE_ID>> placeholder
    if (position.after === '<<PREVIOUS_NODE_ID>>') {
      if (!this.lastCreatedNodeId) {
        // If this is the first operation, find the last node before FinishNode
        console.log('[ScenarioIntentProcessor] <<PREVIOUS_NODE_ID>> used on first operation - finding last node in scenario');
        const finishNode = Object.values(this.scenario).find(n => n.type === 'FinishNode');
        if (finishNode && finishNode.prevNodes && finishNode.prevNodes.length > 0) {
          const lastNodeId = finishNode.prevNodes[0];
          console.log('[ScenarioIntentProcessor] Using last node before FinishNode:', lastNodeId);
          position.after = lastNodeId;
        } else {
          throw new Error('Cannot use <<PREVIOUS_NODE_ID>> - no node was previously created and could not find last node in scenario');
        }
      } else {
        position.after = this.lastCreatedNodeId;
      }
    }

    // Handle "after" position
    if (position.after) {
      // Check both existing scenario AND newly created nodes in patches
      let afterNode = this.scenario[position.after];
      if (!afterNode && patches && patches.add && patches.add[position.after]) {
        afterNode = patches.add[position.after];
      }
      if (!afterNode) {
        throw new Error(`Node ${position.after} not found in scenario or newly created nodes`);
      }
      return {
        prevNodeId: position.after,
        nextNodeId: afterNode.nextNodes?.[0],
        branchId: afterNode.branchId,
        parentBranchId: afterNode.parentBranchId
      };
    }

    // Handle "before" position
    if (position.before) {
      // Check both existing scenario AND newly created nodes in patches
      let beforeNode = this.scenario[position.before];
      if (!beforeNode && patches && patches.add && patches.add[position.before]) {
        beforeNode = patches.add[position.before];
      }
      if (!beforeNode) {
        throw new Error(`Node ${position.before} not found in scenario or newly created nodes`);
      }
      return {
        prevNodeId: beforeNode.prevNodes?.[0],
        nextNodeId: position.before,
        branchId: beforeNode.branchId,
        parentBranchId: beforeNode.parentBranchId
      };
    }

    // Handle "start" position (after StartNode)
    if (position.start) {
      const startNode = Object.values(this.scenario).find(n => n.type === 'StartNode');
      if (!startNode) {
        throw new Error('StartNode not found in scenario');
      }
      return {
        prevNodeId: startNode.id,
        nextNodeId: startNode.nextNodes?.[0],
        branchId: '1',
        parentBranchId: ''
      };
    }

    // Handle "end" position (before FinishNode)
    if (position.end) {
      const finishNode = Object.values(this.scenario).find(n => n.type === 'FinishNode');
      if (!finishNode) {
        throw new Error('FinishNode not found in scenario');
      }
      return {
        prevNodeId: finishNode.prevNodes?.[0],
        nextNodeId: finishNode.id,
        branchId: '1',
        parentBranchId: ''
      };
    }

    throw new Error('Invalid position specification - must have after, before, start, or end');
  }

  /**
   * Update bidirectional links between nodes (flow-designer pattern)
   *
   * @param {Object} patches - Patches object to update
   * @param {string} prevNodeId - Previous node ID
   * @param {string} nextNodeId - Next node ID
   * @param {string} newNodeId - New node being inserted
   */
  updateBidirectionalLinks(patches, prevNodeId, nextNodeId, newNodeId) {
    console.log('[ScenarioIntentProcessor] updateBidirectionalLinks:', {
      prevNodeId,
      nextNodeId,
      newNodeId
    });

    // Initialize update entries if needed
    if (!patches.update[prevNodeId]) {
      patches.update[prevNodeId] = { links: {} };
    }
    if (!patches.update[nextNodeId]) {
      patches.update[nextNodeId] = { links: {} };
    }

    // Update previous node's nextNodes to point to new node
    patches.update[prevNodeId].links.nextNodes = [newNodeId];

    // Update next node's prevNodes to point to new node
    patches.update[nextNodeId].links.prevNodes = [newNodeId];
  }

  /**
   * Get default data for a node type (based on flow-designer node factories)
   *
   * @param {string} nodeType - Node type (e.g., "message:info", "task:id")
   * @returns {Object} - Default data object for the node type
   */
  /**
   * Create the correct articles structure for message:suggestions nodes
   * Handles both simplified LLM input and complex direct structure
   *
   * @param {Object} data - Operation data
   * @returns {Object} - Properly formatted articles object
   */
  createArticlesStructure(data) {
    // If articles structure is already provided, use it as-is
    if (data.articles && typeof data.articles === 'object' && data.articles.type) {
      return data.articles;
    }

    // Otherwise, build structure from simplified parameters
    // LLM can provide: articleIds, categoryIds, tagIds, maxNumber
    const hasArticleIds = data.articleIds && Array.isArray(data.articleIds) && data.articleIds.length > 0;
    const hasCategoryIds = data.categoryIds && Array.isArray(data.categoryIds) && data.categoryIds.length > 0;
    const hasTagIds = data.tagIds && Array.isArray(data.tagIds) && data.tagIds.length > 0;

    // Individually selected (specific article IDs)
    if (hasArticleIds) {
      return {
        type: 'ids',
        ids: data.articleIds,
        filters: {
          categories: [],
          tags: [],
          maxNumber: data.maxNumber || 5
        }
      };
    }

    // Dynamically selected (filtered by categories/tags)
    if (hasCategoryIds || hasTagIds) {
      return {
        type: 'filters',
        ids: [],
        filters: {
          categories: data.categoryIds || [],
          tags: data.tagIds || [],
          maxNumber: data.maxNumber || 5
        }
      };
    }

    // Default: empty filters structure
    return {
      type: 'filters',
      ids: [],
      filters: {
        categories: [],
        tags: [],
        maxNumber: 5
      }
    };
  }

  /**
   * Create the correct facts structure for fit:facts nodes
   * Handles both simplified LLM input and complex direct structure
   *
   * @param {Object} data - Operation data
   * @returns {Object} - Properly formatted facts object
   */
  createFactsStructure(data) {
    // If facts structure is already provided, use it as-is
    if (data.facts && typeof data.facts === 'object' && data.facts.type) {
      return data.facts;
    }

    // Otherwise, build structure from simplified parameters
    // LLM can provide: factIds, categoryIds, tagIds
    const hasFactIds = data.factIds && Array.isArray(data.factIds) && data.factIds.length > 0;
    const hasCategoryIds = data.categoryIds && Array.isArray(data.categoryIds) && data.categoryIds.length > 0;
    const hasTagIds = data.tagIds && Array.isArray(data.tagIds) && data.tagIds.length > 0;

    // Individually selected (specific fact IDs)
    if (hasFactIds) {
      return {
        type: 'ids',
        ids: data.factIds,
        filters: {
          categories: [],
          tags: []
        }
      };
    }

    // Dynamically selected (filtered by categories/tags)
    if (hasCategoryIds || hasTagIds) {
      return {
        type: 'filters',
        ids: [],
        filters: {
          categories: data.categoryIds || [],
          tags: data.tagIds || []
        }
      };
    }

    // Default: empty filters structure
    return {
      type: 'filters',
      ids: [],
      filters: {
        categories: [],
        tags: []
      }
    };
  }

  getDefaultDataForNodeType(nodeType) {
    console.log('[ScenarioIntentProcessor] getDefaultDataForNodeType:', nodeType);

    const defaults = {
      nodeType: nodeType
    };

    // Task nodes (use title, not label)
    if (nodeType.startsWith('task:')) {
      return {
        ...defaults,
        title: '',
        titleDescription: 'Add task',
        message: '',
        activity: '',
        rootLabelField: 'nodeType',
        cardType: 'default',
        isDraggable: true
      };
    }

    // Special nodes that use title instead of label
    if (nodeType === 'message:suggestions' || nodeType === 'fit:facts') {
      return {
        ...defaults,
        title: '',
        titleDescription: nodeType === 'message:suggestions' ? 'Send message' : 'Show facts',
        message: '',
        rootLabelField: 'title',
        cardType: 'default',
        isDraggable: true,
        cardWidth: '450'
      };
    }

    // Most message nodes use label
    return {
      ...defaults,
      label: '',
      message: ''
    };
  }

  /**
   * Get node type class (based on flow-designer node types)
   *
   * @param {string} nodeType - Node type (e.g., "message:info")
   * @returns {string} - Node class name
   */
  getNodeTypeClass(nodeType) {
    if (nodeType === 'start') return 'StartNode';
    if (nodeType === 'finish') return 'FinishNode';

    if (nodeType.startsWith('if:')) {
      if (nodeType === 'if:condition') return 'IfConditionNode';
      if (nodeType === 'if:condition-behavior') return 'IfConditionBehaviorNode';
      if (nodeType === 'if:branch') return 'IfBranchNode';
      if (nodeType === 'if:condition-close') return 'IfCloseNode';
    }

    // Default: all other nodes are GenericNode
    return 'GenericNode';
  }

  /**
   * Generate a valid UUID v4
   *
   * @returns {string} - UUID v4 string
   */
  generateUUID() {
    return uuidv4();
  }
}

/**
 * Convenience function to process intent without instantiating the class
 *
 * @param {Object} intent - Intent object
 * @param {Object} scenario - Current scenario
 * @param {Object} nodeDefinitions - Node definitions
 * @param {Array} tasks - Available tasks
 * @returns {Object} - { patches, errors }
 */
export function processScenarioIntent(intent, scenario, nodeDefinitions, tasks) {
  const processor = new ScenarioIntentProcessor(scenario, nodeDefinitions, tasks);
  return processor.processIntent(intent);
}
